import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useFela } from 'react-fela';
import { CHROMECAST_STATES } from '../../utils';
import ConnectedIcon from './ConnectedIcon';
import ConnectingIcon from './ConnectingIcon';
import DisconnectedIcon from './DisconnectedIcon';
import { usePlayer } from '../../../PlayerContext';
import { smallScreen, largeScreen } from '../../../../common/mixins';
import platform, { OS_TYPES } from '../../../../player/utils/platform';

const containerRules = ({ theme, state, isPlayingAds, isMobile }) => ({
  color: theme.color.buttonColor,
  cursor: 'pointer',
  display: 'flex',
  position: 'absolute',
  zIndex: 1,
  top: isPlayingAds ? '7rem' : theme.margin.large,
  padding: `${theme.margin.fine} ${theme.margin.small}`,
  borderRadius: '5px',
  backgroundColor: theme.color.buttonBackground,
  opacity: state === CHROMECAST_STATES.CONNECTED ? theme.opacity.hovered : theme.opacity.enabled,
  transition: `opacity ${theme.transition.default}, color ${theme.transition.default}`,
  ':hover': {
    opacity: theme.opacity.hovered,
    color: state === CHROMECAST_STATES.CONNECTED ? theme.color.buttonColorHover : theme.color.buttonColor,
  },
  extend: [
    {
      condition: isMobile,
      style: {
        opacity: theme.opacity.hovered,
      },
    },
    smallScreen({
      right: theme.margin.medium,
      top: isPlayingAds ? '4rem' : theme.margin.medium,
    }),
    largeScreen({
      top: isPlayingAds ? '5rem' : theme.margin.large,
    }),
    {
      condition: theme.origDirection === 'ltr',
      style: {
        right: theme.margin.xxlarge,
      },
    },
    {
      condition: theme.origDirection === 'rtl',
      style: {
        left: theme.margin.xxlarge,
      },
    },
  ],
});

function getChromecastIcon(chromecastState, size) {
  switch (chromecastState) {
    case CHROMECAST_STATES.CONNECTED:
      return (
        <ConnectedIcon size={size} />
      );
    case CHROMECAST_STATES.CONNECTING:
      return (
        <ConnectingIcon size={size} />
      );
    case CHROMECAST_STATES.DISCONNECTED:
      return (
        <DisconnectedIcon size={size} />
      );
    // When it's 'Unavailable' the Chromecast button should not be displayed.
    // https://developers.google.com/cast/docs/design_checklist/cast-button
    case CHROMECAST_STATES.UNAVAILABLE:
    default:
      return null;
  }
}

function ChromecastButton({ size, isPlayingAds = false }) {
  const player = usePlayer();
  const chromecastState = useSelector(state => state.playback.chromecastState);
  const isMobile = [OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os);
  const { css } = useFela({ state: chromecastState, isPlayingAds, isMobile });

  const icon = getChromecastIcon(chromecastState, size);
  if (!icon) {
    return null;
  }

  return (
    <div className={css(containerRules)} onClick={() => player.invokeChromecast()}>
      {icon}
    </div>
  );
}

ChromecastButton.propTypes = {
  size: PropTypes.string,
  isPlayingAds: PropTypes.bool,
};

export default React.memo(ChromecastButton);
